import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PricingCard from '../components/PricingCard';

interface Subscription {
  plan: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

interface Usage {
  tasks: { used: number; limit: number; percentage: number };
  agents: { used: number; limit: number; percentage: number };
  users: { used: number; limit: number; percentage: number };
}

const planDetails = {
  free: { name: 'Free', price: 0, features: ['1 user', '3 agents', '100 tasks/month'] },
  start: { name: 'Start', price: 0, features: ['5 users', '10 agents', '1,000 tasks/month'] },
  growth: { name: 'Growth', price: 99, features: ['25 users', '50 agents', '10,000 tasks/month'] },
  pro: { name: 'Pro', price: 249, features: ['100 users', 'Unlimited agents', '50,000 tasks/month'] },
  enterprise: { name: 'Enterprise', price: null, features: ['Unlimited everything'] },
};

export default function Billing() {
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [displayPlan, setDisplayPlan] = useState<string>(() => {
    const planParam = searchParams.get('plan');
    return planParam ? planParam.toLowerCase() : 'free';
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetchSubscription();
    fetchUsage();
    fetchInvoices();
  }, []);

  useEffect(() => {
    const planParam = searchParams.get('plan');
    if (planParam) {
      setDisplayPlan(planParam.toLowerCase());
    }
  }, [searchParams]);

  const fetchSubscription = async () => {
    try {
      const response = await fetch('/api/billing/subscription', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await fetch('/api/billing/usage', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await fetch('/api/billing/invoices', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const handleUpgrade = async (plan: string) => {
    try {
      const response = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          price_id: `price_${plan}_monthly`,
          success_url: `${window.location.origin}/billing?success=true`,
          cancel_url: `${window.location.origin}/billing?canceled=true`,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to start checkout:', error);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          return_url: `${window.location.origin}/billing`,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel? Your subscription will remain active until the end of the billing period.')) {
      return;
    }

    try {
      const response = await fetch('/api/billing/cancel', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        fetchSubscription();
        alert('Subscription will be canceled at the end of the billing period.');
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  const handleSwitchPlan = (plan: string) => {
    setDisplayPlan(plan);
    setSuccessMessage(`Switched to ${planDetails[plan as keyof typeof planDetails]?.name || plan}.`);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setSuccessMessage(null), 4000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const currentPlan = displayPlan || subscription?.plan || 'free';
  const planInfo = planDetails[currentPlan as keyof typeof planDetails] || planDetails.free;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">Billing & Subscription</h1>

      {successMessage && (
        <div className="mb-6 p-4 rounded-lg bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 font-medium">
          {successMessage}
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Current Plan</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                Current Plan
              </span>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {subscription?.status === 'active' ? 'Your subscription is active' : 'You are on the free plan'}
            </p>
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {planInfo.price === null ? 'Custom' : planInfo.price === 0 ? 'Free' : `$${planInfo.price}`}
            </span>
            {planInfo.price !== null && planInfo.price > 0 && (
              <span className="text-gray-500">/month</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            subscription?.status === 'active'
              ? 'bg-green-100 text-green-800'
              :               'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {subscription?.status || 'free'}
          </span>
          {subscription?.cancel_at_period_end && (
            <span className="text-sm text-orange-600">
              Cancels on {new Date(subscription.current_period_end!).toLocaleDateString()}
            </span>
          )}
        </div>

        <div className="flex gap-4">
          {currentPlan === 'free' ? (
            <button
              onClick={() => handleUpgrade('growth')}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Upgrade Plan
            </button>
          ) : (
            <>
              <button
                onClick={handleManageSubscription}
                className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-6 py-2 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Manage Subscription
              </button>
              {!subscription?.cancel_at_period_end && (
                <button
                  onClick={handleCancelSubscription}
                  className="text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors"
                >
                  Cancel Subscription
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Usage Meters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Tasks Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Tasks</h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600 dark:text-gray-400">This month</span>
              <span className="font-medium">
                {usage?.tasks.used || 0} / {usage?.tasks.limit || '∞'}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  (usage?.tasks.percentage || 0) >= 90 ? 'bg-red-500' : 'bg-purple-600'
                }`}
                style={{ width: `${Math.min(usage?.tasks.percentage || 0, 100)}%` }}
              ></div>
            </div>
          </div>
          {(usage?.tasks.percentage || 0) >= 90 && (
            <p className="text-sm text-red-600">Approaching limit</p>
          )}
        </div>

        {/* Agents Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Agents</h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600 dark:text-gray-400">Active agents</span>
              <span className="font-medium">
                {usage?.agents.used || 0} / {usage?.agents.limit || '∞'}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  (usage?.agents.percentage || 0) >= 90 ? 'bg-red-500' : 'bg-purple-600'
                }`}
                style={{ width: `${Math.min(usage?.agents.percentage || 0, 100)}%` }}
              ></div>
            </div>
          </div>
          {(usage?.agents.percentage || 0) >= 90 && (
            <p className="text-sm text-red-600">Approaching limit</p>
          )}
        </div>

        {/* Users Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Users</h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600 dark:text-gray-400">Team members</span>
              <span className="font-medium">
                {usage?.users.used || 0} / {usage?.users.limit || '∞'}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  (usage?.users.percentage || 0) >= 90 ? 'bg-red-500' : 'bg-purple-600'
                }`}
                style={{ width: `${Math.min(usage?.users.percentage || 0, 100)}%` }}
              ></div>
            </div>
          </div>
          {(usage?.users.percentage || 0) >= 90 && (
            <p className="text-sm text-red-600">Approaching limit</p>
          )}
        </div>
      </div>

      {/* Upgrade Options */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(['start', 'growth', 'pro', 'enterprise'] as const).map((planKey) => {
            const details = planDetails[planKey];
            const isCurrent = currentPlan === planKey;
            return (
              <div key={planKey} className="relative">
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                      Current Plan
                    </span>
                  </div>
                )}
                <PricingCard
                  name={details.name}
                  price={details.price}
                  features={details.features}
                  isPopular={planKey === 'growth'}
                  isSelected={isCurrent}
                  onSelect={() => (isCurrent ? undefined : handleSwitchPlan(planKey))}
                  buttonText={isCurrent ? 'Current Plan' : planKey === 'enterprise' ? 'Contact Sales' : 'Switch Plan'}
                  trial={planKey === 'start'}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoice History */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Invoice History</h2>
        {invoices.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-400">No invoices yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Amount</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {new Date(invoice.created * 1000).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                      ${(invoice.amount_paid / 100).toFixed(2)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:underline"
                      >
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
